import { Component, Inject, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { SubscriptionList } from '@vendasta/rx-utils';
import { Pipeline } from '@vendasta/sales-common';
import { StageInterface } from '@vendasta/sales-opportunities';
import { Observable, ReplaySubject, firstValueFrom } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { PipelineMultiSelectService } from './pipeline-multi-select.service';

export interface PipelineDialogData {
  pipelines: Pipeline[];
  pipelineStage: StageInterface;
}

@Component({
  selector: 'sales-ui-pipeline-multi-select',
  templateUrl: './pipeline-multi-select.component.html',
  styleUrls: ['./pipeline-multi-select.component.scss'],
})
export class PipelineMultiSelectComponent implements OnDestroy {
  selectablePipelines$: Observable<Pipeline[]>;
  pipelineControl: UntypedFormControl;
  pipelineStageControl: UntypedFormControl;
  private readonly subscriptions = SubscriptionList.new();

  private selectablePipelinesStages$$ = new ReplaySubject<StageInterface[]>(1);
  public selectablePipelinesStages$ = this.selectablePipelinesStages$$.asObservable();
  private checkedPipelinesIds$$ = new ReplaySubject<Pipeline[]>(1);
  public checkedPipelinesIds$ = this.checkedPipelinesIds$$
    .asObservable()
    .pipe(map((checkedPipelinesIds) => checkedPipelinesIds.map((pipeline) => pipeline.id)));
  private selectedPipelineStage$$ = new ReplaySubject<StageInterface>(1);
  public selectedPipelineStage$ = this.selectedPipelineStage$$.asObservable();

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: PipelineDialogData,
    private readonly store: PipelineMultiSelectService,
    private readonly dialog: MatDialog,
  ) {
    this.selectablePipelines$ = this.store.selectablePipelines$;
    this.checkedPipelinesIds$$.next(data.pipelines);
    this.pipelineControl = new UntypedFormControl(data.pipelines);
    this.pipelineStageControl = new UntypedFormControl(data.pipelineStage, []);
    this.selectablePipelinesStages$ = this.checkedPipelinesIds$$.pipe(
      map((pipelineIds) => (pipelineIds.length !== 1 ? null : pipelineIds[0])),
      withLatestFrom(this.selectablePipelines$),
      map(([selectedPipeline, pipelines]) => pipelines.find((pipeline) => pipeline?.id === selectedPipeline?.id)),
      map((pipeline) => pipeline?.stages),
    );
    this.pipelineControl.valueChanges.subscribe((selectedPipelines) => {
      if (selectedPipelines.length === 1) {
        return this.pipelineStageControl.enable();
      }
      this.pipelineStageControl.disable();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  public pipelineIdComparison(option: Pipeline, value: Pipeline): boolean {
    return Boolean(option) && Boolean(value) ? option.id === value.id : false;
  }

  async handlePipelineSelection(pipelineIds: string[]): Promise<void> {
    const pipelines = await firstValueFrom(this.selectablePipelines$);

    const selectedPipelines: Pipeline[] = [];
    for (const pipelineId of pipelineIds) {
      const found = pipelines.find((p) => p.id === pipelineId);
      if (found !== undefined) {
        selectedPipelines.push(found);
      }
    }
    this.store.setPipelineSelection(selectedPipelines);
    this.store.setPipelineStageSelection(this.pipelineStageControl.value);
    this.dialog.closeAll();
  }

  setCheckedPipelines(selectedPipelines: Pipeline[]): void {
    this.checkedPipelinesIds$$.next(selectedPipelines);
    if (selectedPipelines.length > 1) {
      this.pipelineStageControl.setValue(null);
    }
  }

  setSelectedPipelineStage(selectedStage: StageInterface): void {
    this.selectedPipelineStage$$.next(selectedStage);
  }
}
